import React from 'react';
import { Funnel, X } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All sources' },
  { value: '835', label: '835 (ERA)' },
  { value: '277CA', label: '277CA' },
  { value: 'TA1', label: 'TA1 (Envelope)' },
];

const TRIAGE_OPTIONS = [
  { value: 'all', label: 'All triage states' },
  { value: 'open', label: 'Open (needs action)' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'voided', label: 'Voided' },
];

/**
 * Filter row above the denials table. Source / triage / dates / search.
 * CARC + 277CA filters are driven from TopReasonsPanel clicks instead of
 * being typed in here.
 */
const DenialsFilters = ({ filters, onChange, onReset }) => {
  const activeChips = [];
  if (filters.carc_code) activeChips.push({ key: 'carc_code', label: `CARC ${filters.carc_code}` });
  if (filters.error_code) activeChips.push({ key: 'error_code', label: `277 ${filters.error_code}` });
  if (filters.search) activeChips.push({ key: 'search', label: `“${filters.search}”` });

  return (
    <Card className="border-slate-200">
      <CardContent className="pt-6 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <Input
              placeholder="Search patient or claim #…"
              value={filters.search}
              onChange={(e) => onChange('search', e.target.value)}
              data-testid="denials-search-input"
            />
          </div>
          <Select
            value={filters.source || 'all'}
            onValueChange={(v) => onChange('source', v === 'all' ? '' : v)}
          >
            <SelectTrigger data-testid="denials-source-filter">
              <Funnel size={14} className="mr-2 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.triage_status || 'all'}
            onValueChange={(v) => onChange('triage_status', v === 'all' ? '' : v)}
          >
            <SelectTrigger data-testid="denials-triage-filter">
              <Funnel size={14} className="mr-2 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRIAGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={onReset}
            className="border-slate-200"
            data-testid="denials-reset-filters-btn"
          >
            Reset
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Service date from</label>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => onChange('date_from', e.target.value)}
              data-testid="denials-date-from"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Service date to</label>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => onChange('date_to', e.target.value)}
              data-testid="denials-date-to"
            />
          </div>
        </div>
        {activeChips.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="denials-active-chips">
            <span className="text-xs text-slate-500 self-center">Filtered by:</span>
            {activeChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => onChange(chip.key, '')}
                className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 rounded px-2 py-1 hover:bg-indigo-100 flex items-center gap-1"
                data-testid={`chip-${chip.key}`}
              >
                {chip.label}
                <X size={12} />
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DenialsFilters;
